import {ArtifactType, DecodeScoreDetail, DecodeTeleopBaseLocation} from "ftc-common/javaTypes";
import {classes as commonClasses} from "ftc-common/typescript-generator-info.json"

export type ScoreType = DecodeScoreDetail & {
    movementRPProgress: [number, number],
    goalRPProgress: [number, number],
    patternRPProgress: [number, number],
}
export type ScoreTypeSetValue = <K extends keyof ScoreType>(key: K, value: ScoreType[K]) => void

// TODO make these configurable maybe (or just document the calculator is always for qualifier-level events?)
const MOVEMENT_RP_THRESHOLD = 16;
const GOAL_RP_THRESHOLD = 36;
const PATTERN_RP_THRESHOLD = 18;

export const defaultScore : ScoreType = {
    clazz: commonClasses.find((cc) => cc.typeName === 'DecodeScoreDetail')!.javaClass,
    adjust: 0,
    autoClassifiedArtifacts: 0,
    autoOverflowArtifacts: 0,
    autoClassifierState: new Array(9).fill(ArtifactType.NONE),
    robot1Auto: false,
    robot2Auto: false,
    teleopClassifiedArtifacts: 0,
    teleopOverflowArtifacts: 0,
    teleopDepotArtifacts: 0,
    teleopClassifierState: new Array(9).fill(ArtifactType.NONE),
    robot1Teleop: DecodeTeleopBaseLocation.NONE,
    robot2Teleop: DecodeTeleopBaseLocation.NONE,
    minorFouls: 0,
    majorFouls: 0,
    autoLeavePoints: 0,
    autoArtifactPoints: 0,
    autoPatternPoints: 0,
    teleopArtifactPoints: 0,
    teleopDepotPoints: 0,
    teleopPatternPoints: 0,
    teleopBasePoints: 0,
    autoPoints: 0,
    teleopPoints: 0,
    foulPointsCommitted: 0,
    preFoulTotal: 0,
    violations: [],
    ownMajorFouls: 0,
    ownMinorFouls: 0,
    otherMajorFouls: 0,
    otherMinorFouls: 0,
    hrMajorFouls: 0,
    hrMinorFouls: 0,
    movementRP: false,
    goalRP: false,
    patternRP: false,
    movementRPProgress: [0, MOVEMENT_RP_THRESHOLD],
    goalRPProgress: [0, GOAL_RP_THRESHOLD],
    patternRPProgress: [0, PATTERN_RP_THRESHOLD],
}

const AUTO_LEAVE_VALUE = 3;
const AUTO_CLASSIFIED_ARTIFACT_VALUE = 3;
const AUTO_OVERFLOW_ARTIFACT_VALUE = 1;
const AUTO_MOTIF_MATCH_VALUE = 2;

const TELEOP_CLASSIFIED_ARTIFACT_VALUE = 3;
const TELEOP_OVERFLOW_ARTIFACT_VALUE = 1;
const TELEOP_DEPOT_ARTIFACT_VALUE = 1;
const TELEOP_MOTIF_MATCH_VALUE = 2;
const TELEOP_PARTIAL_BASE_VALUE = 5;
const TELEOP_COMPLETE_BASE_VALUE = 10;
const TELEOP_BASE_VALUES = {
    [DecodeTeleopBaseLocation.NONE]: 0,
    [DecodeTeleopBaseLocation.PARTIAL]: TELEOP_PARTIAL_BASE_VALUE,
    [DecodeTeleopBaseLocation.FULL]: TELEOP_COMPLETE_BASE_VALUE
};
const TELEOP_BASE_BONUS_VALUE = 10;

const MOTIFS = [
    [null, null, null],
    [ArtifactType.GREEN, ArtifactType.PURPLE, ArtifactType.PURPLE],
    [ArtifactType.PURPLE, ArtifactType.GREEN, ArtifactType.PURPLE],
    [ArtifactType.PURPLE, ArtifactType.PURPLE, ArtifactType.GREEN]
];

const MAJOR_FOUL_VALUE = 15;
const MINOR_FOUL_VALUE = 5;

export function calculateScoreBreakdown(score: ScoreType, random: number) {
    let motifIndex = 0;
    if(random > 0) {
        motifIndex = random;
    }
    const motif = MOTIFS[motifIndex];

    score.autoLeavePoints = (score.robot1Auto ? AUTO_LEAVE_VALUE : 0) + (score.robot2Auto ? AUTO_LEAVE_VALUE : 0);
    score.autoArtifactPoints = AUTO_CLASSIFIED_ARTIFACT_VALUE * score.autoClassifiedArtifacts
        + AUTO_OVERFLOW_ARTIFACT_VALUE * score.autoOverflowArtifacts;
    let autoPatternMatches = 0;
    for(let i = 0; i < score.autoClassifierState.length; i++) {
        if(score.autoClassifierState[i] == motif[i % motif.length]) {
            autoPatternMatches++;
        }
    }
    score.autoPatternPoints = AUTO_MOTIF_MATCH_VALUE * autoPatternMatches;
    score.autoPoints = score.autoLeavePoints + score.autoArtifactPoints + score.autoPatternPoints;

    score.teleopArtifactPoints = TELEOP_CLASSIFIED_ARTIFACT_VALUE * score.teleopClassifiedArtifacts
        + TELEOP_OVERFLOW_ARTIFACT_VALUE * score.teleopOverflowArtifacts;
    score.teleopDepotPoints = TELEOP_DEPOT_ARTIFACT_VALUE * score.teleopDepotArtifacts;
    let teleopPatternMatches = 0;
    for(let i = 0; i < score.teleopClassifierState.length; i++) {
        if(score.teleopClassifierState[i] == motif[i % motif.length]) {
            teleopPatternMatches++;
        }
    }
    score.teleopPatternPoints = TELEOP_MOTIF_MATCH_VALUE * teleopPatternMatches;
    score.teleopBasePoints = TELEOP_BASE_VALUES[score.robot1Teleop] + TELEOP_BASE_VALUES[score.robot2Teleop] + (score.robot1Teleop == DecodeTeleopBaseLocation.FULL && score.robot2Teleop == DecodeTeleopBaseLocation.FULL ? TELEOP_BASE_BONUS_VALUE : 0);
    score.teleopPoints = score.teleopArtifactPoints + score.teleopDepotPoints + score.teleopPatternPoints + score.teleopBasePoints;

    score.movementRPProgress = [score.autoLeavePoints + score.teleopBasePoints, MOVEMENT_RP_THRESHOLD];
    score.movementRP = score.movementRPProgress[0] >= score.movementRPProgress[1];
    score.goalRPProgress = [score.autoClassifiedArtifacts + score.autoOverflowArtifacts + score.teleopClassifiedArtifacts + score.teleopOverflowArtifacts, GOAL_RP_THRESHOLD];
    score.goalRP = score.goalRPProgress[0] >= score.goalRPProgress[1];
    score.patternRPProgress = [score.autoPatternPoints + score.teleopPatternPoints, PATTERN_RP_THRESHOLD];
    score.patternRP = score.patternRPProgress[0] >= score.patternRPProgress[1];

    score.foulPointsCommitted = score.minorFouls * MINOR_FOUL_VALUE + score.majorFouls * MAJOR_FOUL_VALUE;

    score.preFoulTotal = score.autoPoints + score.teleopPoints;
}
